'use client';

import React from 'react';

interface Props {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** Pages either side of the current one to show (default 1: `… c-1 c c+1 …`). */
  siblingCount?: number;
  /**
   * True while the listing is still topping up. The control renders at its full size (so it does not
   * resize under the visitor when the hits land) but cannot be used to page into products that are
   * not in memory yet.
   */
  disabled?: boolean;
}

const DOTS = 'dots';

/**
 * Build a windowed page list with ellipses so the control never overflows (esp. on mobile),
 * regardless of how many pages there are: e.g. `1 … 4 5 6 … 20`. Deterministic (no media query),
 * so it renders identically on the server and the client.
 */
const buildRange = (page: number, totalPages: number, siblingCount: number): Array<number | typeof DOTS> => {
  // first + last + current + 2*siblings + 2 dots. If everything fits, show every page.
  const totalToShow = siblingCount * 2 + 5;
  if (totalPages <= totalToShow) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, totalPages);
  const showLeftDots = leftSibling > 2;
  const showRightDots = rightSibling < totalPages - 1;

  const range = (start: number, end: number) => Array.from({ length: end - start + 1 }, (_, i) => start + i);

  if (!showLeftDots && showRightDots) {
    return [...range(1, 3 + 2 * siblingCount), DOTS, totalPages];
  }
  if (showLeftDots && !showRightDots) {
    return [1, DOTS, ...range(totalPages - (2 + 2 * siblingCount), totalPages)];
  }
  return [1, DOTS, ...range(leftSibling, rightSibling), DOTS, totalPages];
};

/**
 * Client pagination controls for the SSR grid. Page 1's tiles are rendered server-side; the rest
 * arrive with the dataset top-up, after which this only toggles which page is visible. Rendered in
 * the SSR HTML with page 1 active; becomes interactive on hydration once the listing is complete.
 */
const SsrPagination: React.FC<Props> = ({ page, totalPages, onChange, siblingCount = 1, disabled = false }) => {
  if (totalPages <= 1) return null;

  const items = buildRange(page, totalPages, siblingCount);
  const itemClass = `flex items-center justify-center px-8 py-6 text-14 md:px-12 md:py-8 md:text-16 ${
    disabled ? 'cursor-default' : 'cursor-pointer'
  }`;

  return (
    <div className="mt-32 flex w-full justify-center" aria-busy={disabled || undefined}>
      <ul className="flex flex-row items-center gap-4 md:gap-8">
        <li className={`border border-gray-300 ${page <= 1 ? 'opacity-50' : ''}`}>
          <button
            type="button"
            className={itemClass}
            aria-label="Previous page"
            disabled={disabled || page <= 1}
            onClick={() => onChange(page - 1)}
          >
            ‹
          </button>
        </li>
        {items.map((item, i) =>
          item === DOTS ? (
            <li key={`dots-${i}`} className="flex items-center justify-center px-4 text-gray-500 md:px-8" aria-hidden>
              …
            </li>
          ) : (
            <li key={item} className={`border ${item === page ? 'border-black bg-black text-white' : 'border-gray-300'}`}>
              <button
                type="button"
                className={itemClass}
                aria-label={`Page ${item}`}
                aria-current={item === page ? 'page' : undefined}
                disabled={disabled}
                onClick={() => onChange(item)}
              >
                {item}
              </button>
            </li>
          ),
        )}
        <li className={`border border-gray-300 ${page >= totalPages ? 'opacity-50' : ''}`}>
          <button
            type="button"
            className={itemClass}
            aria-label="Next page"
            disabled={disabled || page >= totalPages}
            onClick={() => onChange(page + 1)}
          >
            ›
          </button>
        </li>
      </ul>
    </div>
  );
};

export default SsrPagination;
